/**
 * Form Submission Utility
 * Handles form submissions to both FormSubmit (for email) and Google Sheets (for lead management)
 */

export interface FormSubmitResult {
  success: boolean;
  error?: string;
}

/**
 * Submit form data to FormSubmit and Google Sheets
 * @param formData - The form data to submit
 * @param formSubmitEmail - Your email address for FormSubmit
 * @param googleSheetUrl - Your Google Sheets Web App URL (optional)
 * @returns Promise with success status and error message if failed
 */
export async function submitForm(
  formData: Record<string, any>,
  formSubmitEmail: string,
  googleSheetUrl?: string
): Promise<FormSubmitResult> {
  try {
    // Submit to the app server endpoint which queues/sends to FormSubmit and Google Sheets.
    const payload = {
      ...formData,
      timestamp: new Date().toISOString(),
      submittedAt: new Date().toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata'
      }),
      _subject: `New Lead: ${formData.fullName || formData.name}`
    };

    // Use a short client-side timeout so the UI feels snappy even if external services are slow
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 8000);

    const res = await fetch('/api/lead', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload),
      signal: controller.signal
    });

    clearTimeout(timeout);

    if (!res.ok) {
      let json: any = null;
      try { json = await res.json(); } catch (e) { /* ignore */ }
      throw new Error(json?.error || 'Failed to submit lead');
    }

    return { success: true };
  } catch (error) {
    console.error('Form submission error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to submit form. Please try again.'
    };
  }
}

/**
 * Configuration for FormSubmit
 * Replace with your actual email address
 */
export const FORMSUBMIT_EMAIL = 'mohammed.siddique44@gmail.com'; // TODO: Replace with your email

/**
 * Configuration for Google Sheets
 * Replace with your Google Apps Script Web App URL
 */
export const GOOGLE_SHEETS_URL = 'https://script.google.com/macros/s/AKfycbwCcE_pQkBLMdBg7RVEJ-Cjmdi61Jg-SZveefAbdm5uj9nOqFN4x5Jr7Qkp3vAs_xoAvA/exec'; 


/**
 * Opens WhatsApp with a pre-filled message as a fallback for form submission failures.
 * @param formData - The form data to include in the message
 */
export function openWhatsAppFallback(formData: Record<string, any>) {
  const phoneNumber = '919886276722';
  
  // Format the message based on available fields
  let message = 'Hi Hayat Interiors, I tried to submit a form on your website but it failed. Here are my details:\n\n';
  
  if (formData.fullName || formData.name) {
    message += `*Name:* ${formData.fullName || formData.name}\n`;
  }
  if (formData.phone) {
    message += `*Phone:* ${formData.phone}\n`;
  }
  if (formData.email) {
    message += `*Email:* ${formData.email}\n`;
  }
  if (formData.location) {
    message += `*Location:* ${formData.location}\n`;
  }
  if (formData.projectType) {
    message += `*Project Type:* ${formData.projectType}\n`;
  }
  if (formData.budget) {
    message += `*Budget:* ${formData.budget}\n`;
  }
  if (formData.message) {
    message += `*Message:* ${formData.message}\n`;
  }
  
  message += '\nPlease help me with my enquiry.';
  
  const encodedMessage = encodeURIComponent(message);
  const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
  
  // Open in a new tab
  if (typeof window !== 'undefined') {
    window.open(whatsappUrl, '_blank');
  }
}
